import React, { useState } from 'react';

import { useLanguage } from '../LanguageContext';

const getGuestName = (guestId, allGuests, t) => {
  const guest = Array.isArray(allGuests)
    ? allGuests.find((g) => String(g.id) === String(guestId))
    : null;
  return guest?.name || `${t('rsvp_form.guest')} ${guestId}`;
};

const buttonStyle = (selected) => {
  return selected ? 'btn-rsvp-choice-selected' : 'btn-rsvp-choice';
};

export default function IndividualChoiceStep({
  formData,
  setFormData,
  allGuests = [],
  responseKey,
  attendanceKey,
  guestOptionKey,
  title,
  subtitle,
  options = [],
  hasTextboxFor,
  onNext,
  onBack,
}) {
  const { t } = useLanguage();
  const individualResponses = formData.individualResponses || {};
  const guestIds = Object.keys(individualResponses);

  // Filter guest IDs based on attendance key and guest option key if provided
  const filteredGuestIds = guestIds.filter((guestId) => {
    const guest = Array.isArray(allGuests)
      ? allGuests.find((g) => String(g.id) === String(guestId))
      : null;
    const isOptionEnabled = guestOptionKey
      ? guest?.[guestOptionKey] !== false
      : true;
    const isAttending = attendanceKey
      ? individualResponses[guestId]?.[attendanceKey] === true
      : true;
    return isOptionEnabled && isAttending;
  });

  // Track the active option selection for each guest
  const [selectedOptions, setSelectedOptions] = useState(() => {
    const initial = {};
    for (const guestId of filteredGuestIds) {
      const val = individualResponses[guestId]?.[responseKey];
      if (hasTextboxFor && val !== null && val !== undefined && val !== '') {
        const matchesStandardOption = options.some(
          (opt) => opt.value !== hasTextboxFor && opt.value === val,
        );
        if (matchesStandardOption) {
          initial[guestId] = val;
        } else {
          initial[guestId] = hasTextboxFor;
        }
      } else {
        initial[guestId] = val || '';
      }
    }
    return initial;
  });

  const handleSelect = (guestId, optionValue) => {
    setSelectedOptions((prev) => ({
      ...prev,
      [guestId]: optionValue,
    }));

    if (hasTextboxFor && optionValue === hasTextboxFor) {
      const currentVal = individualResponses[guestId]?.[responseKey];
      const matchesStandardOption = options.some(
        (opt) => opt.value !== hasTextboxFor && opt.value === currentVal,
      );
      if (matchesStandardOption || !currentVal) {
        updateValue(guestId, '');
      }
    } else {
      updateValue(guestId, optionValue);
    }
  };

  const updateValue = (guestId, val) => {
    setFormData((state) => ({
      ...state,
      individualResponses: {
        ...state.individualResponses,
        [guestId]: {
          ...state.individualResponses?.[guestId],
          [responseKey]: val,
        },
      },
    }));
  };

  if (filteredGuestIds.length === 0) {
    return null;
  }

  const hasMultipleGuests = guestIds.length > 1;

  if (!hasMultipleGuests) {
    const singleGuestId = filteredGuestIds[0];
    const currentSelection = selectedOptions[singleGuestId];
    const textValue = individualResponses[singleGuestId]?.[responseKey] || '';

    return (
      <div className="space-y-6 max-w-md">
        <div className="space-y-3 text-center">
          <h2 className="text-4xl text-content-h1">{title}</h2>
          {subtitle && <p className="text-lg text-content-c1">{subtitle}</p>}
        </div>
        <div className="grid gap-3">
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => {
                handleSelect(singleGuestId, opt.value);
                if (!hasTextboxFor || opt.value !== hasTextboxFor) {
                  setTimeout(() => onNext(), 500);
                }
              }}
              className={buttonStyle(currentSelection === opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {hasTextboxFor && currentSelection === hasTextboxFor && (
          <div className="space-y-2">
            <textarea
              value={textValue}
              onChange={(e) => updateValue(singleGuestId, e.target.value)}
              className="input-wedding"
              placeholder={t('rsvp_form.describe_placeholder')}
              rows={4}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-3 text-center">
        <h2 className="text-4xl text-content-h1">{title}</h2>
        {subtitle && <p className="text-lg text-content-c1">{subtitle}</p>}
      </div>

      <div className="space-y-6 divide-y divide-wedding-divider">
        {filteredGuestIds.map((guestId, index) => {
          const currentSelection = selectedOptions[guestId];
          const textValue = individualResponses[guestId]?.[responseKey] || '';

          return (
            <div
              key={guestId}
              className={`space-y-3 ${index > 0 ? 'pt-6' : ''}`}
            >
              <div className="text-2xl text-content-h2">
                {getGuestName(guestId, allGuests, t)}
              </div>

              <div className="flex flex-row flex-wrap gap-2 text-xl">
                {options.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => handleSelect(guestId, opt.value)}
                    className={buttonStyle(currentSelection === opt.value)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              {hasTextboxFor && currentSelection === hasTextboxFor && (
                <div className="pt-2">
                  <textarea
                    value={textValue}
                    onChange={(e) => updateValue(guestId, e.target.value)}
                    className="input-wedding"
                    placeholder={t('rsvp_form.describe_placeholder')}
                    rows={3}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
